export interface ProductTemplate {
  slug: string;
  name: string;
  tagline: string;
  description: string;
  specSlug: string;
  category: string;
  /** Per-feature context explaining how this real product implements the feature */
  featureNotes: Record<string, string>;
}

export const productTemplates: ProductTemplate[] = [
  {
    slug: 'slack',
    name: 'Slack',
    tagline: 'Team messaging & channels',
    description:
      'Slack pioneered channel-based messaging for teams. It balances real-time communication with async workflows through threading, and integrates deeply with developer tooling.',
    specSlug: 'slack-like',
    category: 'communication',
    featureNotes: {
      'user-system':
        'Workspace-based org model with SSO for enterprise. Users have profiles with custom status and timezone awareness.',
      messaging:
        'Channels are the core unit. Threads prevent channel noise. DMs exist but channels are encouraged. Full message history on paid plans.',
      notifications:
        'Highly configurable per-channel with DND scheduling. Desktop, mobile push, and email fallback. Keyword alerts.',
      search:
        'Full-text search across all messages and files. Enterprise has compliance-grade message archiving.',
      'file-storage':
        'File sharing inline in messages. 5 GB free, unlimited on paid plans. Deep integrations with Google Drive and Dropbox.',
    },
  },
  {
    slug: 'discord',
    name: 'Discord',
    tagline: 'Communities with voice & text',
    description:
      'Discord started as gaming chat and became the default community platform. Servers with role-gated channels, voice rooms, and forums define the architecture.',
    specSlug: 'slack-like',
    category: 'communication',
    featureNotes: {
      'user-system':
        'Server-based community model. Role system for permissions is first-class. One global account spans all servers.',
      messaging:
        'Text channels + voice channels side-by-side in servers. DMs exist. Threads and forum channels for structured discussion.',
      notifications:
        'Mention-based by default. Per-server and per-channel mute controls. Mobile push and in-app.',
      search:
        'Basic message search. Less emphasis than Slack — Discord optimises for real-time over retrieval.',
      'file-storage':
        '8 MB upload limit free (25 MB with Nitro). No deep cloud integrations — raw file upload only.',
    },
  },
  {
    slug: 'notion',
    name: 'Notion',
    tagline: 'Wiki, docs & databases in one',
    description:
      'Notion combines documents, wikis, and relational databases into a block-based editor. The "page as database row" model makes it uniquely flexible for teams.',
    specSlug: 'note-taking',
    category: 'productivity',
    featureNotes: {
      'user-system':
        'Workspace-based with guest access. Team plans support fine-grained page-level permissions.',
      search:
        'Full-text search across all workspace content. AI search on paid plans. Quick-find is a core UX pattern.',
      'file-storage':
        'Images and files embedded inline in blocks. Unlimited file uploads on Team plan and above.',
      notifications:
        'Comment mentions and page update notifications. Email digest. Less real-time than chat tools.',
      'api-access':
        'Public API for reading and writing databases and pages. Powers many integration tools and automation workflows.',
    },
  },
  {
    slug: 'linear',
    name: 'Linear',
    tagline: 'Issue tracking for software teams',
    description:
      'Linear is built for speed and developer experience with a rigid but powerful model: teams, cycles, projects, and issues with strict status workflows.',
    specSlug: 'project-mgmt',
    category: 'productivity',
    featureNotes: {
      'user-system':
        'Workspace + team hierarchy. Member and admin roles. Linear is opinionated about org structure to enforce consistency.',
      notifications:
        'Activity feed and email for mentions, assignments, and status changes. Slack integration is first-class.',
      search:
        'Fast keyboard-driven search. Saved filters and views are the primary power-user interface.',
      'file-storage':
        'File attachments on issues and comments. Not a storage-first product.',
      messaging:
        'Comments on issues only — not a chat product. Relies on Slack for real-time communication.',
    },
  },
  {
    slug: 'github',
    name: 'GitHub',
    tagline: 'Code hosting & collaboration',
    description:
      'GitHub is built around git repositories with pull requests, issues, Actions CI/CD, and an enormous API surface that makes almost everything automatable.',
    specSlug: 'dev-tool',
    category: 'developer',
    featureNotes: {
      'user-system':
        'Individual + organization accounts. Fine-grained repository permissions. SAML SSO on Enterprise.',
      'api-access':
        'REST and GraphQL APIs covering virtually all functionality. GitHub Apps and OAuth Apps for third-party integrations.',
      payments:
        'Generous free tier. Paid plans per seat. GitHub Actions billed by compute minutes.',
      analytics:
        'Contributor graphs, traffic data, and Actions usage dashboards. Org-level insights on Enterprise.',
      'admin-panel':
        'Org settings, team management, repo policies, and SAML SSO configuration.',
      notifications:
        'Watch/unwatch repos. Mention-based. Email or web. High volume — most developers filter aggressively.',
    },
  },
  {
    slug: 'stripe',
    name: 'Stripe',
    tagline: 'Payments API for developers',
    description:
      'Stripe is the canonical developer-first API product. Everything is API-first, the dashboard is secondary, and developer experience is the primary differentiator.',
    specSlug: 'dev-tool',
    category: 'developer',
    featureNotes: {
      'user-system':
        'Account with team members and roles. API keys are the primary auth mechanism — user sessions are for the dashboard only.',
      'api-access':
        'REST API is the entire product. Versioned with long deprecation windows. Idempotency keys, webhooks, and strict typing.',
      payments:
        'Stripe IS the payments product. Supports 135+ currencies, subscriptions, invoicing, and Connect for marketplace payouts.',
      analytics:
        'Revenue dashboards, churn metrics, MRR/ARR, and cohort analysis. Strong financial analytics built-in.',
      'admin-panel':
        'Stripe Dashboard is the operator interface. Used for refunds, disputes, and customer lookup by non-technical staff.',
      notifications:
        'Webhooks are the primary notification mechanism. Email receipts and account alerts for the team.',
    },
  },
  {
    slug: 'shopify',
    name: 'Shopify',
    tagline: 'E-commerce for everyone',
    description:
      'Shopify is a fully-managed e-commerce platform. Merchants get a storefront, inventory management, and payments out of the box with minimal technical setup.',
    specSlug: 'ecommerce',
    category: 'commerce',
    featureNotes: {
      'user-system':
        'Two user types: merchant (store admin) and customer (shopper). Customer accounts are optional at checkout.',
      payments:
        'Shopify Payments built-in, or third-party gateways. Checkout is heavily optimised. Shop Pay for accelerated repeat purchases.',
      search:
        'Storefront search across products. Predictive search API. AI-powered recommendations on Shopify Plus.',
      notifications:
        'Transactional emails (order confirmation, shipping updates) and marketing emails. SMS on higher plans.',
      'file-storage':
        'Product images and video with CDN delivery. 3D model support for AR product previews.',
    },
  },
  {
    slug: 'airbnb',
    name: 'Airbnb',
    tagline: 'Two-sided rental marketplace',
    description:
      'Airbnb is the blueprint two-sided marketplace. Trust mechanisms, review systems, secure payment escrow, and host/guest messaging define the core architecture.',
    specSlug: 'marketplace',
    category: 'commerce',
    featureNotes: {
      'user-system':
        'Single account with dual roles (guest + host). Identity verification is required before bookings.',
      payments:
        'Split payments: guest charges and host payouts. Escrow model. Security deposits and tax collection handled by Airbnb.',
      search:
        'Location + date + filters is the entire discovery UX. Map view is critical to the product.',
      notifications:
        'Booking requests, messages, and status updates in real-time. Email, SMS, and push for hosts.',
      'file-storage':
        'Listing photos are primary content. Up to 100 photos per listing. Host verification documents.',
      messaging:
        'Guest-host messaging is required before and during a stay. Message templates for common host responses.',
      analytics:
        'Host dashboard: revenue, occupancy rate, views, and booking conversion. Dynamic pricing suggestions.',
    },
  },
  {
    slug: 'twitter',
    name: 'Twitter / X',
    tagline: 'Public short-form social network',
    description:
      'Twitter is a public broadcast network with an asymmetric follow model. The real-time firehose, algorithmic feed, and reply threads define the architecture.',
    specSlug: 'social-app',
    category: 'communication',
    featureNotes: {
      'user-system':
        'Public profiles with follower/following counts. Verified accounts. Asymmetric follows (not mutual friends).',
      notifications:
        'Likes, reposts, replies, and new followers. Both algorithmic and chronological feed options.',
      search:
        'Real-time search of public posts. Trending topics. Operator syntax (#hashtag, from:user, since:date).',
      'file-storage':
        'Images, GIFs, and video in posts. 4 images or 1 video per post. Hosted CDN for all media.',
      messaging:
        'DMs are a secondary feature. Group DMs. End-to-end encryption available.',
      analytics:
        'Post impressions, profile visits, and engagement rates in creator analytics dashboard.',
      'admin-panel':
        'Moderation tools for content removal and account actions. Community Notes crowdsourced fact-checking.',
    },
  },
  {
    slug: 'hubspot',
    name: 'HubSpot',
    tagline: 'CRM & inbound marketing',
    description:
      'HubSpot built its CRM as a free product to drive adoption of marketing and sales tools. It spans contact management, pipelines, automation, and customer support.',
    specSlug: 'crm',
    category: 'productivity',
    featureNotes: {
      'user-system':
        'Contact-centric model. Sales reps, marketers, and managers share a data model with role-based views.',
      search:
        'Contact, company, deal, and ticket search. Saved filter lists and segments. Import/export workflows.',
      notifications:
        'Deal stage changes, task reminders, and email open/click tracking. Daily activity digest emails.',
      'file-storage':
        'Document tracking (prospect PDF open alerts). Email attachments and CRM document storage.',
    },
  },
  {
    slug: 'vercel',
    name: 'Vercel',
    tagline: 'Frontend deployment platform',
    description:
      'Vercel is a developer-first cloud platform for frontend deployment. Git-push-to-deploy, preview deployments, and a global edge network define the developer experience.',
    specSlug: 'dev-tool',
    category: 'developer',
    featureNotes: {
      'user-system':
        'Personal + team accounts. Project and team roles. GitHub/GitLab/Bitbucket OAuth as primary login.',
      'api-access':
        'REST API for deployments, domains, and environment variables. CLI is the primary developer interface.',
      payments:
        'Hobby (free), Pro, and Enterprise tiers. Bandwidth and build minutes as billing dimensions.',
      analytics:
        'Web Analytics, Real Experience Score, and function invocation metrics. Core Web Vitals tracking.',
      'admin-panel':
        'Team dashboard for deployments, domains, members, billing, and usage limits.',
      notifications:
        'Deploy success and failure via email and Slack. Webhook integration for CI/CD pipelines.',
    },
  },
];

export function getProductTemplate(slug: string): ProductTemplate | undefined {
  return productTemplates.find((p) => p.slug === slug);
}

export const PRODUCT_CATEGORY_LABELS: Record<string, string> = {
  communication: 'Communication',
  productivity: 'Productivity',
  commerce: 'Commerce',
  developer: 'Developer Tools',
};
